import React, { Component } from 'react';
import {
  connect,
  ConnectedProps
} from 'react-redux';
import {
  isEmpty,
  toString
} from 'lodash';
import moment from 'moment';
import {
  ELSURLHelper,
  ELSTokenHelper,
  ELSLoggingService,
  ELSCommonUIConstants,
  ELSTokenServiceRegistrar,
} from '@els/els-ui-common-react';
import {
  studyActions,
  UserParams
} from '../../redux/student-study/studentStudy.actions';
import { locationActions } from '../../redux/location/location.actions';
import { locationSelectors } from '../../redux/location/location.selectors';
import { ELSTokenUser } from '../../models/els.dtos';
import { registerMouseFlowService } from '../../components/app/app.config';
import {
  removeCookies
} from '../app-link-redirect/app-link-redirect.utilities';
import { AnalyticsAction } from '../../models/analytics.models';
import { DeepLinkQueryParam } from '../../constants/deep-link.constants';
import { LoginQueryParam } from './login.constants';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import { RoutePath } from '../../components/app/app.constants';

const mapStateToProps = (state) => ({
  location: locationSelectors.getLocation(state),
});

const mapDispatchToProps = {
  fetchCourseSection: studyActions.fetchCourseSection,
  fetchIsbns: studyActions.fetchIsbns,
  fetchAllAppFeatureFlags: studyActions.fetchAllAppFeatureFlags,
  redirect: locationActions.redirect,
  replaceURL: locationActions.replaceURL,
  setIsbns: studyActions.setIsbns,
  setUser: studyActions.setUser,
  setRegisteredToken: studyActions.setRegisteredToken,
  trackAction: studyActions.trackAction,
  setEnableDeepLink: studyActions.setEnableDeepLink,
  fetchEvolveUsers: studyActions.fetchEvolveUsers,
  setEcrIdAction: studyActions.setEcrIdAction,
  navigateToApp: studyActions.navigateToApp,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type LoginPageProps = ConnectedProps<typeof connector> & {
  aaData?: {
    analytics: {
      pageName: string;
      pageType: string;
    };
  };
}

export interface LoginPageState {
  hasError: boolean;
  errorMessage: string;
}

const LoginPageFileName = 'Login.page.tsx';

class LoginPage extends Component<LoginPageProps, LoginPageState> {
  constructor(props) {
    super(props);
    // Remove cookies if left around by other app
    removeCookies();
    this.state = {
      hasError: false,
      errorMessage: ''
    };
  }

  componentDidMount() {

    const {
      location,
      setRegisteredToken,
      setEnableDeepLink,
      setEcrIdAction
    } = this.props;

    const queryString = location.search;
    const courseSectionId = parseInt(ELSURLHelper.getParameterByName(LoginQueryParam.COURSE_ID, queryString), 10) || 0;
    const token = ELSURLHelper.getParameterByName(LoginQueryParam.TOKEN, queryString);
    const isbn = ELSURLHelper.getParameterByName(LoginQueryParam.ISBN, queryString);
    const isDirectAccess = Boolean(token && isbn);
    const ecrId = ELSURLHelper.getParameterByName(LoginQueryParam.ECR_ID, queryString);
    const enableDeepLink = ELSURLHelper.getParameterByName(DeepLinkQueryParam.ENABLE_DEEP_LINK, queryString);

    setEcrIdAction(ecrId);

    if (!this.validateLaunchParameters(courseSectionId, token, isbn, isDirectAccess)) {
      return;
    }

    const tokenExpireDate = ELSTokenHelper.getExpirationDateFromToken(token);
    const now = moment();
    if (now.isAfter(tokenExpireDate)) {
      const errorMessage = `Failed to login via LMS - Launching app by LTI launch with courseSectionId: ${courseSectionId} expired token`;
      this.logError(errorMessage);

      return;
    }

    ELSTokenServiceRegistrar.register(token);
    setRegisteredToken(token);
    setEnableDeepLink(enableDeepLink === 'true');

    const tokenUser: ELSTokenUser = ELSTokenHelper.getUser();

    if (isEmpty(tokenUser)) {
      const errorMessage = `Failed to login via LMS - Launching app by LTI launch with courseSectionId: ${courseSectionId} invalid token`;
      this.logError(errorMessage);

      return;
    }

    const tokenCourseSectionId = tokenUser.appParams && tokenUser.appParams.courseId;
    const resolvedCourseSectionId = courseSectionId || tokenCourseSectionId || null;

    if (!courseSectionId && !isDirectAccess) {
      const errorMessage = 'Failed to login via LMS - Launching app by LTI launch: missing courseSectionId';
      this.logError(errorMessage);

      return;
    }

    if (tokenUser.role !== ELSCommonUIConstants.userType.Student && tokenUser.role !== ELSCommonUIConstants.userType.Instructor) {
      const errorMessage = 'User role is not supported';
      this.logError(errorMessage);

      return;
    }

    if (isDirectAccess && tokenUser.role !== ELSCommonUIConstants.userType.Student) {
      this.logError('Failed to login via Direct Access: unsupported user role');

      return;
    }

    const userParams = {
      userId: tokenUser.userId.toString(),
      courseId: resolvedCourseSectionId ? toString(resolvedCourseSectionId) : null,
      roleId: tokenUser.role,
      isbns: isbn || tokenUser.appParams.isbns
    };
    this.init(userParams, isDirectAccess);
  }

  validateLaunchParameters = (courseSectionId, token, isbn, isDirectAccess) => {
    if (token && !courseSectionId && !isbn) {
      this.logError('Failed to login via Direct Access: missing ISBN');
      return false;
    }

    if (!courseSectionId && !isDirectAccess) {
      this.logError('Failed to login via LMS - Launching app by LTI launch: missing courseSectionId');
      return false;
    }

    if (!token) {
      this.logError(`Failed to login via LMS - Launching app by LTI launch with courseSectionId: ${courseSectionId} missing token`);
      return false;
    }

    return true;
  };

  logError = errorMessage => {
    ELSLoggingService.error(LoginPageFileName, errorMessage);
    this.setState({
      hasError: true,
      errorMessage
    });
  };

  init(userParams: UserParams, isDirectAccess = false) {
    const {
      fetchCourseSection,
      fetchAllAppFeatureFlags,
      fetchIsbns,
      setIsbns,
      setUser,
      trackAction,
      navigateToApp,
      replaceURL,
    } = this.props;

    setUser(userParams);
    registerMouseFlowService();

    if (isDirectAccess && userParams.roleId === ELSCommonUIConstants.userType.Student) {
      Promise.all([
        fetchAllAppFeatureFlags(),
        userParams.courseId ? fetchCourseSection(userParams.courseId) : Promise.resolve(null),
        fetchIsbns(userParams.courseId, userParams.userId)
      ])
        .then(([, , questionSets]) => {
          const isValidIsbn = questionSets && questionSets.some(questionSet => questionSet.isbn === userParams.isbns);
          if (!isValidIsbn) {
            this.logError('Failed to login via Direct Access: invalid ISBN');
            return;
          }
          setIsbns(userParams.isbns);
          replaceURL(RoutePath.EAQ_STUDENT_STUDY);
        })
        .catch(() => this.logError('Failed to launch Student Study App: request failed'));
      return;
    }

    setIsbns(userParams.isbns);
    trackAction({
      action: AnalyticsAction.AI_CHAT_LAUNCH_REDIRECTED,
      props: {
        ref: 'LOGIN'
      }
    });

    navigateToApp({
      action: AppAction.LAUNCH,
      app: Application.NHE_AI,
      body: {
        targetPage: 'CHATBOT'
      },
    });
  }

  render() {
    const { hasError, errorMessage } = this.state;

    if (hasError) {
      return (
        <div className="u-els-padding-3x">
          <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary" data-testid="ssa-login-error-message">{errorMessage}</h2>
        </div>
      );
    }

    return (
      <div className="u-els-padding-3x">
        <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary" data-testid="ssa-login-loading-message">Loading</h2>
      </div>
    );
  }
}

export { LoginPage };

export default connector(LoginPage);
